'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@/components/ui/dialog'
import { X, Play } from 'phosphor-react'
import { Button } from '@/components/ui/button'

interface VideoModalProps {
  src?: string
  dataPath?: string
  alt: string
  className?: string
  children?: React.ReactNode
  poster?: string
  requireAuth?: boolean
  onOpenChange?: (open: boolean) => void
}

function mimeForUrl(url: string): string {
  const path = url.split('?')[0].toLowerCase()
  if (path.endsWith('.webm')) return 'video/webm'
  if (path.endsWith('.mov')) return 'video/quicktime'
  return 'video/mp4'
}

function buildPathCandidates(dataPath: string): string[] {
  const cleaned = dataPath.replace(/^\/+/, '')
  const withoutVideos = cleaned.replace(/^videos\//, '')
  const baseNoExt = withoutVideos.replace(/\.(mov|mp4|webm)$/i, '')
  const ext = (withoutVideos.match(/\.(mov|mp4|webm)$/i)?.[1] || 'mp4').toLowerCase()

  // Requested ext first, then both common containers — R2 may only have .mov or only .mp4
  return [
    ...new Set([
      `videos/${baseNoExt}.${ext}`,
      `videos/${baseNoExt}.mp4`,
      `videos/${baseNoExt}.mov`,
      `videos/${baseNoExt}.webm`,
    ]),
  ]
}

function buildPublicFallbacks(dataPath: string): string[] {
  const cleaned = dataPath.replace(/^\/+/, '').replace(/^videos\//, '')
  const baseNoExt = cleaned.replace(/\.(mov|mp4|webm)$/i, '')
  return [
    `/assets/${baseNoExt}.mp4`,
    `/assets/${baseNoExt}.mov`,
    `/assets/${cleaned}`,
    `/${cleaned}`,
  ]
}

function looksLikeVideoResponse(res: Response): boolean {
  if (!(res.ok || res.status === 206)) return false
  const type = (res.headers.get('content-type') || '').toLowerCase()
  if (type.includes('video/') || type.includes('application/octet-stream')) return true
  // Reject SPA HTML fallbacks that Vite returns as 200 for missing assets
  if (type.includes('text/html')) return false
  const len = Number(res.headers.get('content-length') || 0)
  return len > 50_000
}

async function urlExists(url: string, timeoutMs = 2500): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const head = await fetch(url, { method: 'HEAD', signal: controller.signal })
    if (looksLikeVideoResponse(head)) return true
    const get = await fetch(url, {
      method: 'GET',
      headers: { Range: 'bytes=0-0' },
      signal: controller.signal,
    })
    return looksLikeVideoResponse(get)
  } catch {
    return false
  } finally {
    clearTimeout(timer)
  }
}

export default function VideoModal({
  src,
  dataPath,
  alt,
  className = '',
  children,
  poster,
  requireAuth = false,
  onOpenChange,
}: VideoModalProps) {
  const [open, setOpen] = useState(false)
  const [videoUrl, setVideoUrl] = useState<string | null>(src || null)
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    src ? 'ready' : dataPath ? 'idle' : 'error'
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange]
  )

  const resolveUrl = useCallback(async () => {
    if (src) {
      setVideoUrl(src)
      setLoadState('ready')
      setErrorMessage(null)
      return
    }

    if (!dataPath) {
      setLoadState('error')
      setErrorMessage('No video source provided.')
      return
    }

    setLoadState('loading')
    setErrorMessage(null)

    const onViteOnlyPort =
      typeof window !== 'undefined' &&
      (window.location.port === '5173' || window.location.port === '5174') &&
      window.location.port !== '8888'

    const r2Paths = buildPathCandidates(dataPath)
    let lastError: Error | null = null

    try {
      const { getSignedUrl } = await import('../getSignedUrl')
      for (const path of r2Paths) {
        try {
          const url = await getSignedUrl(path, requireAuth)
          setVideoUrl(url)
          setLoadState('ready')
          setErrorMessage(null)
          return
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error))
        }
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
    }

    for (const pub of buildPublicFallbacks(dataPath)) {
      if (await urlExists(pub)) {
        setVideoUrl(pub)
        setLoadState('ready')
        setErrorMessage(null)
        return
      }
    }

    console.error('Failed to load video:', lastError)
    setVideoUrl(null)
    setLoadState('error')
    setErrorMessage(
      onViteOnlyPort
        ? 'Videos need Netlify Functions. Stop Vite and run npm run dev:full, then open http://localhost:8888'
        : lastError?.message ||
            'Video could not load. Locally, run npm run dev:full (netlify dev) so R2 signed URLs work.'
    )
  }, [dataPath, requireAuth, src])

  useEffect(() => {
    void resolveUrl()
  }, [resolveUrl])

  useEffect(() => {
    if (open && (loadState === 'error' || (loadState !== 'ready' && !videoUrl))) {
      void resolveUrl()
    }
  }, [open, loadState, videoUrl, resolveUrl])

  const tryPlay = useCallback((video: HTMLVideoElement) => {
    video.muted = true
    video.defaultMuted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
    const playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Native controls remain available if autoplay is blocked
      })
    }
  }, [])

  // Bind play once the <video> mounts while the dialog is open
  const setVideoNode = useCallback(
    (node: HTMLVideoElement | null) => {
      videoRef.current = node
      if (node && open && videoUrl) {
        tryPlay(node)
      }
    },
    [open, videoUrl, tryPlay]
  )

  useEffect(() => {
    if (!open || !videoUrl) return
    const video = videoRef.current
    if (!video) return

    const onReady = () => tryPlay(video)
    video.addEventListener('loadeddata', onReady)
    video.addEventListener('canplay', onReady)
    tryPlay(video)

    return () => {
      video.removeEventListener('loadeddata', onReady)
      video.removeEventListener('canplay', onReady)
      video.pause()
    }
  }, [open, videoUrl, tryPlay])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div
          data-video-trigger
          className={`cursor-pointer ${className}`}
          role="button"
          aria-label={`Play video: ${alt}`}
          tabIndex={0}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {children || (
            <div className="relative w-full aspect-video bg-[var(--brutal-kraft)] overflow-hidden">
              {poster ? (
                <img src={poster} alt={alt} className="absolute inset-0 w-full h-full object-cover object-[center_25%]" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--brutal-muted)]">
                  <Play weight="duotone" className="w-16 h-16 mb-2" />
                  <p className="text-sm">Click to play video</p>
                </div>
              )}
              <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                <div className="brutal-chip bg-[var(--brutal-panel)] p-4 shadow-[var(--brutal-shadow-sm)]">
                  <Play weight="fill" className="w-8 h-8 text-[var(--brutal-ink)]" />
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black border-2 border-[var(--brutal-ink)] rounded-lg overflow-hidden p-0 !w-[min(96vw,960px)] !max-w-[96vw] !h-auto"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <div className="relative flex items-center justify-center min-h-[240px] bg-black">
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-20 bg-black/60 hover:bg-black/80 text-white border border-white/40 rounded-full w-10 h-10"
            onClick={() => handleOpenChange(false)}
            aria-label="Close video modal"
          >
            <X weight="duotone" className="w-5 h-5" />
          </Button>

          {loadState === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 p-10 text-white/90 text-sm">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <p>Loading video…</p>
            </div>
          )}

          {loadState === 'error' && (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center max-w-md">
              <p className="text-white font-semibold">Video couldn&apos;t play</p>
              <p className="text-white/70 text-sm leading-relaxed">
                {errorMessage ||
                  'Signed URL failed. For local R2 videos, run netlify dev (port 8888).'}
              </p>
              <Button type="button" className="brutal-btn mt-2" onClick={() => void resolveUrl()}>
                Retry
              </Button>
            </div>
          )}

          {loadState === 'ready' && videoUrl && (
            <video
              key={videoUrl}
              ref={setVideoNode}
              className="w-full max-h-[85vh] rounded-lg bg-black"
              src={videoUrl}
              controls
              playsInline
              muted
              autoPlay
              preload="auto"
              poster={poster}
              aria-label={alt}
            >
              <source src={videoUrl} type={mimeForUrl(videoUrl)} />
              {/* Safari often needs an explicit QuickTime source when R2 serves .mov */}
              {videoUrl.includes('.mov') && (
                <source src={videoUrl} type="video/quicktime" />
              )}
              Your browser does not support the video tag.
            </video>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
